import React, { useEffect, useMemo } from 'react';
import UnauthenticatedRootView from '../views/unauthenticated-root-view/UnauthenticatedRootView';
import LoggedOutTemplate from '../templates/LoggedOutTemplate';
import LoggedInTemplate from '../templates/LoggedInTemplate';
import AuthenticationStateErrorView from '../views/authentication-state-error-view/AuthenticationStateErrorView';
import Prompt from '../ui/prompt/Prompt';
import TokenStateErrorView from '../views/token-state-error-view/TokenStateErrorView';
import DcsPromo from '../views/promo/DcsPromo';
import ChatViewLayout, { ChatViewLayoutProps } from '../layouts/chat-view-layout/ChatViewLayout';
import ChatView from '../views/chat-view/ChatView';
import CatalogView from '../views/catalog-view/CatalogView';
import InformationView from '../views/information-view/InformationView';
import NotFound from '../elements/not-found/NotFound';
import useLabels from '../hooks/useLabels';
import adminRoutes from '../features/dashboards/admin/Routes';
import useInfo from '../hooks/useInfo';
import { Navigate, Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import { AuthenticationState, useAppInfo } from '../contexts/app-info/AppInfoContext';
import { TokenState, useAuth } from '../contexts/auth/AuthContext';
import { setupGlobalAnchorClickHandler } from '../helpers/hashRouting';
import { EventHandlerModel } from '../services/EventHandler';
import { defaultPersonaKey } from '../constants/consts';
import { useAdaptive } from '../contexts/adaptive/AdaptiveContext';
import style from './Routing.module.scss';
import { useFileDownloadToast } from '../hooks/useFileDownloadToast';
import Loader from '../ui/loader/Loader';

// import PromptManagementView from '../features/prompts/elements/prompt-management-view/PromptManagementView';
// import PromptView from '../features/prompts/elements/prompt-view/PromptView';


import { verifyCallback } from '../contexts/auth/OauthInnerAuthProvider';
import ChatViewLayoutPlus1 from '../layouts/chat-view-layout/ChatViewLayoutPlus1';

type Props = {};

export enum DynamicRouteParams {
    chatId = 'chatId',
    objectId = 'objectId',
    objectType = 'objectType',
}

export type DynamicRouteProperties = {
    [DynamicRouteParams.chatId]?: string;
    [DynamicRouteParams.objectId]?: string;
    [DynamicRouteParams.objectType]?: string;
};

export enum AppRoute {
    root = '/',
    info = '/info',
    promptManagement = '/prompt-management',
    basicAssistant = `/${defaultPersonaKey}`,
    chat = '/chat',
    catalog = '/catalog',
    prompts = '/prompts',
    chatItem = `/chat/:chatId`,
    dcsPimPersonaPromo = '/dcs/promo',
    callback = '/callback',
    // plus1 = '/plus1',
    logout = '/logout',
}

// Add this component to handle the callback
const CallbackHandler = () => {
    //console.log'CallbackHandler rendered');
    useEffect(() => {
        verifyCallback();
    }, []);

    return <Loader isVisible isGlobal />;
};

export const getChatItemRoute = (chatId: string) => AppRoute.chatItem.replace(`:${DynamicRouteParams.chatId}`, chatId);

const Routing: React.FC<Props> = () => {
    const labels = useLabels();
    const { login, tokenState } = useAuth();

    const navigate = useNavigate();
    const location = useLocation();
    let{ preferences } = useAppInfo();

    const { allowedPersonaOptions: personas } = useInfo();
    //console.log'Allowed personas in Routing:', personas);
       // const renderPromptView = (props: ChatViewLayoutProps) => <PromptView {...props} />;
    // const renderPromptManagementView = (props: ChatViewLayoutProps) => <PromptManagementView {...props} />;

    const { catalogFavorites } = useAdaptive();

    const renderChatView = (props: ChatViewLayoutProps) => <ChatView {...props} />;
    const renderCatalogView = (props: ChatViewLayoutProps) => <CatalogView {...props} />;

    // const renderPromptView = (props: ChatViewLayoutProps) => <PromptView {...props} />;
    // const renderPromptManagementView = (props: ChatViewLayoutProps) => <PromptManagementView {...props} />;


    const renderPersonaRoutes = useMemo(() => {
        return personas.map((persona) => {
            return <Route key={persona.key} path={'/' + persona.route} element={<ChatViewLayout key='same-key' children={renderChatView} newPrompt newPromptOrigin={persona.key} />} />;
        });
    }, [personas]);

    //console.log'Personas:', personas);
    const firstFavorite = useMemo(() => {
        let p = catalogFavorites.firstFavorite || preferences?.personas_pinned?.[0];

        if (p) {
            const persona = personas?.find(x => x.key === p);
            p = persona?.route;
        }

        return p || defaultPersonaKey;
    }, [catalogFavorites.firstFavorite, preferences?.personas_pinned, personas]);

    const renderAdminRoutes = useMemo(() => adminRoutes(), []);

    // After DIP sign-out returns to /logout, go to root so the landing page shows instead of re-triggering DIP login.
    useEffect(() => {
        if (location.pathname === AppRoute.logout) {
            navigate(AppRoute.root, { replace: true });
        }
    }, [location.pathname, navigate]);

    const renderAuthenticated = useMemo<React.ReactNode>(() => {
        //console.log'Rendering authenticated routes. First favorite:', firstFavorite);
        return <div>
            <Routes>
            <Route path={AppRoute.info} element={<InformationView />} />
            <Route path={AppRoute.root} element={<Navigate to={firstFavorite} replace={true} />} />
            <Route path={AppRoute.chat} element={<Navigate to={'/' + firstFavorite} replace={true} />} />
            <Route path={AppRoute.dcsPimPersonaPromo} element={<DcsPromo />} />
            <Route path={AppRoute.catalog} element={<ChatViewLayout children={renderCatalogView} newPrompt key='same-key' />} />
            <Route path={AppRoute.callback} element={<CallbackHandler />} />
            <Route path={AppRoute.logout} element={<CallbackHandler />} />
            {/* <Route path={AppRoute.logout} element={<Navigate to={AppRoute.root} replace={true} />} /> */}
            {/* <Route path={AppRoute.plus1} element={<ChatViewLayoutPlus1 children={renderChatView} newPrompt key='same-key1' />} /> */}

            {/* <Route path={AppRoute.prompts} element={<ChatViewLayout children={renderPromptView} />} /> */}
            {/* <Route path={AppRoute.promptManagement} element={<ChatViewLayout children={renderPromptManagementView} />} /> */}

            {renderPersonaRoutes}
            {renderAdminRoutes}

            <Route path={AppRoute.chatItem + '/*'} element={<ChatViewLayout children={renderChatView} />} />
            <Route path='/*' element={<NotFound />} />
            {/* <Route path={AppRoute.basicAssistant} element={<Navigate to={'/' + firstFavorite} replace={true} />} /> */}
            <Route path={AppRoute.basicAssistant} element={<Navigate to={'/' + firstFavorite} replace={true} />} />
            </Routes>

            {tokenState === TokenState.RequiresInteraction && <Prompt
                className={style['token-expired-prompt']}
                isOpen={tokenState === TokenState.RequiresInteraction}
                headline={labels.tokenExpiredHeadline}
                body={labels.tokenExpiredDescription}
                closeOnBackdropClick={false}
                closeOnEscape={false}
                primaryButton={{
                    label: labels.login,
                    onClick: login,
                }}
            />}
        </div>;
    }, [firstFavorite, labels.login, labels.tokenExpiredDescription, labels.tokenExpiredHeadline, login, renderAdminRoutes, renderPersonaRoutes, tokenState]);

    const renderLoggedIn = useMemo<React.ReactNode>(() => {
        if (tokenState === TokenState.Valid) {
            return renderAuthenticated;
        }
    }, [renderAuthenticated, tokenState]);

    // Redirect to AppRoute.chat when renderAuthenticated is true (i.e., user is logged in)
    useEffect(() => {
        if (tokenState === TokenState.Valid) {
            //navigate(AppRoute.catalog, { replace: true });
        }
    }, [tokenState]);

    const renderLoggedOut = useMemo<React.ReactNode>(() => {
        if(tokenState !== TokenState.Valid) return <UnauthenticatedRootView />;
    }, [tokenState]);

    return (
        <>
            <GlobalAnchorHandler />
            <LoggedInTemplate>
                {renderLoggedIn}
            </LoggedInTemplate>
            <LoggedOutTemplate>
                {renderLoggedOut}
            </LoggedOutTemplate>
        </>
    );
};

export function GlobalAnchorHandler() {

    const navigate = useNavigate();
    const { download } = useAppInfo();


    const fileDownloadToast = useFileDownloadToast();
    const localAnchorHandler = setupGlobalAnchorClickHandler();
    useEffect(() => {
        const onLocalNavigation = (event: EventHandlerModel<typeof localAnchorHandler>) => {
            if ('download' in event) {
                const downloadPromise = download(event.download);
                fileDownloadToast(downloadPromise);

            } else
                navigate({ pathname: event.path, hash: event.hash });
        };

        localAnchorHandler.on(onLocalNavigation);
        return () => {
            localAnchorHandler.off(onLocalNavigation);
        };
    }, [navigate, download, localAnchorHandler, fileDownloadToast]);

    return <></>;
}

export default Routing;
